import { PeekErrorWrapper } from '@daymade/common-lib/errors';
import { getGlobalUiStore, UIStore } from 'stores/UIStore';
import HttpStatusCode from 'utils/HttpStatusCode';

/** An interface that loosely describes what a failing API response looks like */
export interface LooseApiErrorResponse {
  /** The possible error codes that handleNonOkResponse is expecting to handle. Add more error codes as needed, but this
   * should not include 200, as that implies the calling code did not handle the 200 OK state correctly. */
  status: 400 | 401 | 'error';
  body?: {
    /** The (hopefully) human-friendly error message returned from the API */
    message?: string;
  };
  /** An exception that was caught from the TS Rest API call */
  error?: PeekErrorWrapper;
}

/** Checks that a type never has 200 in it, and causes a compile errors */
type Not200Status<T extends { status: any }> = 200 extends T['status']
  ? { error: 'LooseApiErrorResponse should not include 200' }
  : T;

/** A simple helper for handling TSRest API responses when they aren't successful. This should be used for cases when
 * the code that called the API has nothing better to do with the error response. e.g. generally the API call succeeds
 * and the code just wants to show something to the user when the request failed.
 *
 * Ideally, the calling code should handle any special cases first, e.g. a change password API may return useful error
 * codes to guide the user about how to proceed, rather than just throwing up this error popup. If the calling code
 * has a sensible place to show a generic error message, then handleNonOkResponse does not need to be used.
 *
 * @param response The response from the TSRest API client
 * @param uiStore The UI store, if available. The calling-component may need to be updated to call
 * const uiStore = useUIStore() near the top of the component (i.e don't call useUIStore in a callback or useEffect). If
 * UI store is not provided, then a horrible old school alert dialog is used.
 */
export function handleNonOkResponse(
  response: Not200Status<LooseApiErrorResponse> | undefined,
  uiStore: UIStore | undefined | null
) {
  if (typeof window === 'undefined') {
    // we must be running as part of server-side rendering, we can't provide the browser with any error
    return;
  }

  let message: string;
  if (response?.status == HttpStatusCode.UNAUTHORIZED) {
    // This message will be shown when an API endpoint returned 401.
    message = 'Your session has expired. Please sign out and try again.';
  
  } else if (isNetworkError(response?.error)) {
    // The fetch API can't tell the difference between the browser being disconnected from the internet or our backend
    // servers not being available. Let's try and use the navigator.onLine API
    if (navigator.onLine === false) {
      // The browser is reporting that we are offline, so a message about being offline
      message =
        'It looks like you are offline. Please check your network connection and try again.';
    } else {
      // The browser is reporting that we are online, or the 'navigator.onLine' API doesn't exist. Show a more generic
      // message
      message =
        'It looks like there was a problem connecting to Daymade. Please check your network connection and try again.';
    }
  
  } else if (response?.body?.message) {
    message = 'Oops, something went wrong: ' + response.body.message;
  
  } else if (response?.status == HttpStatusCode.BAD_REQUEST) {
    // This message is deliberately hand-wavey, unique and long, so that we can track down this line of code if it is
    // provided in bug reports. This message will be shown when an API endpoint returned 400, but didn't
    // provide a message
    message = 'Oops, something went wrong. Please try again later.';
  
  } else {
    // This message is deliberately hand-wavey, unique and long, so that we can track down this line of code if it is
    // provided in bug reports. This message will be shown when an API endpoint returned something that wasn't
    // handled, possibly a 500 error
    message = 'Oops, something unexpected happened. Please try again later.';
  }

  if (!uiStore) {
    uiStore = getGlobalUiStore();
  }

  if (uiStore) {
    uiStore.displayToast({
      title: message,
      color: '#FF6600',
      timeout: 9000,
    });
  } else {
    alert(message);
  }
}

/** Returns true if the Error object matches what would be returned by a failed fetch. Usually, this indicates some
 * kind of network problem.
 */
function isNetworkError(error: unknown) {
  return error instanceof TypeError && error.message === 'Failed to fetch';
}
